import { createContext, ReactElement, useEffect, useReducer } from 'react';

// third party
import { jwtDecode } from 'jwt-decode';
import { mutate } from 'swr';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import axios, { refreshAccessToken } from 'utils/axios';
import { loginUser } from 'services/authService';

// types
import { KeyedObject } from 'types';
import { InitialLoginContextProps, JWTContextType } from 'types/auth';


// constant
const initialState: InitialLoginContextProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

function verifyToken(serviceToken: string): boolean {
  if (!serviceToken) {
    return false;
  }

  const decoded: KeyedObject = jwtDecode(serviceToken);

  // Ensure 'exp' exists and compare it to the current timestamp
  if (!decoded.exp) {
    throw new Error("Token does not contain 'exp' property.");
  }

  return decoded.exp > Date.now() / 1000;
}

function setSession(serviceToken?: string | null): void {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete axios.defaults.headers.common.Authorization;
  }
}

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export function JWTProvider({ children }: { children: ReactElement }) {
  const [state, dispatch] = useReducer(accountReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem('serviceToken');
        const storedUser = window.localStorage.getItem('user');

        if (serviceToken && verifyToken(serviceToken)) {
          setSession(serviceToken);

          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: storedUser ? JSON.parse(storedUser) : jwtDecode(serviceToken)
            }
          });
        } else {
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, []);

  const login = async (useLogin: string, usePassword: string) => {
    const { token, user } = await loginUser({ useLogin, usePassword });
    setSession(token);
    localStorage.setItem('user', JSON.stringify(user));
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user
      }
    });
    // Fuerza recarga de menú/permisos (evita SWR con datos viejos o 401 previo)
    void mutate('me/current-user');
  };

  const register = async () => {
    // no hace nada
  };

  const logout = () => {
    setSession(null);
    localStorage.removeItem('user');
    dispatch({ type: LOGOUT });
  };

  // Renueva el token antes de que expire, calculando el tiempo real restante del JWT.
  // Usa setTimeout recursivo en lugar de setInterval fijo para que funcione correctamente
  // tras recargas de página (el setInterval reiniciaba desde 0 y el token expiraba antes).
  //
  // Además del timer proactivo, escucha `visibilitychange`: si el equipo estuvo suspendido o la
  // pestaña estuvo en segundo plano (el navegador puede retrasar el setTimeout más allá del
  // vencimiento real del token), se revalida apenas la pestaña vuelve a estar visible en lugar de
  // esperar a que el timer eventualmente dispare.
  useEffect(() => {
    if (!state.isLoggedIn) return;

    const REFRESH_MARGIN_MS = 5 * 60 * 1000;

    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    let refreshInFlight: Promise<void> | null = null;

    const getRemainingMs = (): number | null => {
      const stored = localStorage.getItem('serviceToken');
      if (!stored) return null;
      try {
        return jwtDecode<{ exp: number }>(stored).exp * 1000 - Date.now();
      } catch {
        return null;
      }
    };

    const runRefresh = (): Promise<void> => {
      if (refreshInFlight) return refreshInFlight;
      refreshInFlight = (async () => {
        let ok = false;
        try {
          await refreshAccessToken();
          ok = true;
        } catch {
          // Un reintento adicional antes de cerrar sesión.
          try {
            await refreshAccessToken();
            ok = true;
          } catch {
            ok = false;
          }
        }
        if (cancelled) return;
        if (ok) {
          scheduleNext(); // re-programar con el nuevo token
        } else {
          logout();
        }
      })().finally(() => { refreshInFlight = null; });
      return refreshInFlight;
    };

    const scheduleNext = () => {
      if (cancelled) return;
      const remaining = getRemainingMs();
      if (remaining === null || remaining <= 0) { logout(); return; }

      // Nunca programar el refresh después del vencimiento real — el piso fijo de 30s que había
      // antes podía hacerlo cuando quedaba menos de ~5.5 min de vida útil del token.
      const delay = remaining > REFRESH_MARGIN_MS
        ? remaining - REFRESH_MARGIN_MS
        : Math.max(remaining - 1000, 0);

      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { void runRefresh(); }, delay);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const remaining = getRemainingMs();
      if (remaining === null || remaining <= 0) { logout(); return; }
      if (remaining <= REFRESH_MARGIN_MS) {
        void runRefresh();
      } else {
        scheduleNext(); // re-sincroniza el timer por si el navegador lo había retrasado
      }
    };

    scheduleNext();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state.isLoggedIn]);

  const resetPassword = async (_email: string) => {};

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return <JWTContext value={{ ...state, login, logout, register, resetPassword, updateProfile }}>{children}</JWTContext>;
}

export default JWTContext;
